export const currencyOptions = [
  { code: "RMB", labelEn: "Renminbi", labelZh: "人民币", symbol: "RMB", minorUnit: 2 },
  { code: "USD", labelEn: "US Dollar", labelZh: "美元", symbol: "USD", minorUnit: 2 },
  { code: "EUR", labelEn: "Euro", labelZh: "欧元", symbol: "EUR", minorUnit: 2 },
  { code: "GBP", labelEn: "British Pound", labelZh: "英镑", symbol: "GBP", minorUnit: 2 },
  { code: "HKD", labelEn: "Hong Kong Dollar", labelZh: "港币", symbol: "HKD", minorUnit: 2 },
  { code: "JPY", labelEn: "Japanese Yen", labelZh: "日元", symbol: "JPY", minorUnit: 0 },
  { code: "SGD", labelEn: "Singapore Dollar", labelZh: "新加坡元", symbol: "SGD", minorUnit: 2 },
] as const;

export type CurrencyCode = (typeof currencyOptions)[number]["code"];

const currencyByCode = new Map<string, (typeof currencyOptions)[number]>(
  currencyOptions.map((currency) => [currency.code, currency]),
);

export function normalizeCurrencyCode(value: unknown): CurrencyCode {
  if (typeof value !== "string") return "RMB";
  const normalized = value.toUpperCase() === "CNY" ? "RMB" : value.toUpperCase();
  return currencyByCode.has(normalized) ? (normalized as CurrencyCode) : "RMB";
}

export function getCurrencyOption(code: unknown) {
  return currencyByCode.get(normalizeCurrencyCode(code)) ?? currencyOptions[0];
}

export function getCurrencyMinorUnit(code: unknown): number {
  return getCurrencyOption(code).minorUnit;
}

export function getExchangeApiCode(code: unknown): string {
  const normalized = normalizeCurrencyCode(code);
  return normalized === "RMB" ? "CNY" : normalized;
}

export function majorToMinor(valueMajor: number | string | null | undefined, code: unknown): number {
  const value = Number(valueMajor);
  const multiplier = 10 ** getCurrencyMinorUnit(code);
  return Number.isFinite(value) ? Math.round(value * multiplier) : 0;
}

export function minorToMajor(valueMinor: number | null | undefined, code: unknown): number {
  const value = Number(valueMinor);
  const divisor = 10 ** getCurrencyMinorUnit(code);
  return Number.isFinite(value) ? value / divisor : 0;
}

export function convertMinorCurrencyPrecision(valueMinor: number | null | undefined, fromCode: unknown, toCode: unknown): number {
  return majorToMinor(minorToMajor(valueMinor, fromCode), toCode);
}

export function formatCurrencyMinor(valueMinor: number | null | undefined, code: unknown): string {
  const currency = getCurrencyOption(code);
  const value = Number.isFinite(Number(valueMinor)) ? Number(valueMinor) : 0;
  const divisor = 10 ** currency.minorUnit;
  const amount = currency.minorUnit === 0 ? value : value / divisor;
  return `${currency.code} ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: currency.minorUnit,
    maximumFractionDigits: currency.minorUnit,
  }).format(amount)}`;
}
